import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queueApi } from '@/features/queue/api';
import { notify } from '@/lib/notify';
import { apiErrorMessage } from '@/api/errors';
import { POLL_FAST } from '@/lib/polling';
import type { QueueMessageParams, QueueMessageActionType, QueueControlActionType } from '@/features/queue/types';

const keys = {
  summary: ['queue', 'summary'] as const,
  overview: ['queue', 'overview'] as const,
  messages: (p: QueueMessageParams) => ['queue', 'messages', p] as const,
  message: (id: string) => ['queue', 'message', id] as const,
};

/**
 * Queue depth summary. The single owner of the `/queue/summary` query — the
 * dashboard's MailQueueWidget consumes this same hook/key, so the endpoint is
 * fetched and polled once regardless of how many surfaces are mounted.
 */
export const useQueueSummary = () =>
  useQuery({ queryKey: keys.summary, queryFn: queueApi.summary, refetchInterval: POLL_FAST });

/* ---- Delivery Center hooks (4.9) ---- */

/** KPIs + throughput trend + bucket breakdown. */
export const useQueueOverview = () =>
  useQuery({ queryKey: keys.overview, queryFn: queueApi.overview, refetchInterval: POLL_FAST, refetchOnWindowFocus: true });

export const useQueueMessages = (p: QueueMessageParams) =>
  useQuery({ queryKey: keys.messages(p), queryFn: () => queueApi.messages(p), placeholderData: (x) => x, refetchInterval: POLL_FAST });

/** Per-message payload is stable while queued — keep it 60s. */
export const useQueueMessageDetail = (id: string | null) =>
  useQuery({ queryKey: keys.message(id ?? ''), queryFn: () => queueApi.message(id!), enabled: !!id, staleTime: 60_000 });

const ACTION_VERB: Record<QueueMessageActionType, string> = {
  retry: 'Retried', force: 'Forced delivery of', move: 'Moved', release: 'Released',
  hold: 'Held', delete: 'Deleted', requeue: 'Requeued',
};

/** Row + bulk actions on specific message(s). */
export function useQueueMessageAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, queueIds, targetQueue }: { action: QueueMessageActionType; queueIds: string[]; targetQueue?: string }) =>
      queueApi.action(action, queueIds, targetQueue),
    onSuccess: ({ affected }, { action }) => {
      qc.invalidateQueries({ queryKey: ['queue'] });
      notify.success(`${ACTION_VERB[action]} ${affected} message${affected === 1 ? '' : 's'}`);
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Queue action failed')),
  });
}

const CONTROL_VERB: Record<QueueControlActionType, string> = {
  pause: 'Paused', resume: 'Resumed', flush: 'Flushing',
};

/** Pause / resume / flush a whole logical queue. */
export function useQueueControl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, queue }: { action: QueueControlActionType; queue: string }) => queueApi.control(action, queue),
    onSuccess: (_r, { action, queue }) => {
      qc.invalidateQueries({ queryKey: ['queue'] });
      notify.success(`${CONTROL_VERB[action]} the ${queue} queue`);
    },
    onError: (e) => notify.error(apiErrorMessage(e, 'Queue control failed')),
  });
}
